import React from 'react';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
  FireIcon,
} from '@heroicons/react/24/outline';

interface SeverityBadgeProps {
  severity: 'low' | 'medium' | 'high' | 'critical';
  size?: 'sm' | 'md' | 'lg';
}

interface StatusBadgeProps {
  status: 'new' | 'processing' | 'analyzed' | 'validated' | 'false_positive' | 'resolved' | 'ignored';
  size?: 'sm' | 'md' | 'lg';
}

interface TypeBadgeProps {
  type: string;
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-2.5 py-1.5 text-sm',
  lg: 'px-3 py-2 text-base',
};

const ICON_SIZES: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
};

const SEVERITY_CONFIG: Record<string, { color: string; icon: React.ComponentType<any>; label: string }> = {
  critical: { color: 'bg-red-100 text-red-800 border-red-200', icon: FireIcon, label: 'Crítica' },
  high: { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: ExclamationTriangleIcon, label: 'Alta' },
  medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: ShieldExclamationIcon, label: 'Média' },
  low: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: InformationCircleIcon, label: 'Baixa' },
};

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  new: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Novo' },
  processing: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Processando' },
  analyzed: { color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'Analisado' },
  validated: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Validado' },
  false_positive: { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Falso Positivo' },
  resolved: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Resolvido' },
  ignored: { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Ignorado' },
};

const TYPE_CONFIG: Record<string, { color: string; label: string }> = {
  phishing: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Phishing' },
  malware: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Malware' },
  data_leak: { color: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Vazamento de Dados' },
  brand_abuse: { color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'Abuso de Marca' },
  social_media: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Mídia Social' },
  dark_web: { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Dark Web' },
  vulnerability: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Vulnerabilidade' },
  suspicious_domain: { color: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Domínio Suspeito' },
  credential_leak: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Vazamento de Credenciais' },
  threat_actor: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Ator de Ameaça' },
  ioc: { color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'IOC' },
  supplier_intel: { color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'Intel de Fornecedor' },
};

const Badge: React.FC<{ color: string; label: string; size?: 'sm' | 'md' | 'lg'; Icon?: React.ComponentType<any> }> = ({
  color,
  label,
  size = 'md',
  Icon,
}) => (
  <span className={`inline-flex items-center ${Icon ? 'gap-1' : ''} rounded-full border font-medium ${color} ${SIZE_CLASSES[size]}`}>
    {Icon ? <Icon className={ICON_SIZES[size]} /> : null}
    {label}
  </span>
);

export const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity, size = 'md' }) => {
  const config = SEVERITY_CONFIG[severity] || {
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: InformationCircleIcon,
    label: 'Desconhecida',
  };
  return <Badge color={config.color} label={config.label} size={size} Icon={config.icon} />;
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md' }) => {
  const config = STATUS_CONFIG[status] || { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Desconhecido' };
  return <Badge color={config.color} label={config.label} size={size} />;
};

export const TypeBadge: React.FC<TypeBadgeProps> = ({ type, size = 'md' }) => {
  const config = TYPE_CONFIG[type] || { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Outro' };
  return <Badge color={config.color} label={config.label} size={size} />;
};
